import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type {
    InfiniteScrollListCursor,
    InfiniteScrollListFetchPage,
    InfiniteScrollListPage,
    InfiniteScrollListSearchTerms,
} from '@/components/ui/infinite-scroll-list.types';
import type {
    SearchFieldPredicate,
    SearchJsonPathPredicate,
} from '@pillar-portal/api-types';
import { tokenizeOmnisearchQueryText } from '@/lib/omnisearch';

export interface UseInfiniteScrollListOptions<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
> {
    queryKeyPrefix: TQueryKeyPrefix;
    pageSize: number;
    fetchPage: InfiniteScrollListFetchPage<TItem, TCursor>;
    parseCursorParam: (pageParam: unknown) => TCursor | undefined;
    fieldPredicates?: ReadonlyArray<SearchFieldPredicate>;
    jsonPathPredicates?: ReadonlyArray<SearchJsonPathPredicate>;
    initialQueryText?: string;
    enabled?: boolean;
    retry?: boolean;
    staleTime?: number;
    gcTime?: number;
    rootMargin?: string;
    threshold?: number;
    observerRootRef?: RefObject<HTMLElement | null>;
}

export interface UseInfiniteScrollListResult<
    TItem,
    TCursor extends InfiniteScrollListCursor,
> {
    queryText: string;
    setQueryText: (nextQueryText: string) => void;
    items: ReadonlyArray<TItem>;
    isLoading: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => Promise<void>;
    error: Error | null;
    loadMoreSentinelRef: React.MutableRefObject<HTMLDivElement | null>;
    pagination:
        | InfiniteScrollListPage<TItem, TCursor>['pagination']
        | null;
}

type InfiniteScrollListQueryKey<TQueryKeyPrefix extends readonly unknown[]> = readonly [
    ...TQueryKeyPrefix,
    string,
    string,
    string,
];

export function useInfiniteScrollList<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
>(
    options: UseInfiniteScrollListOptions<TItem, TCursor, TQueryKeyPrefix>
): UseInfiniteScrollListResult<TItem, TCursor> {
    const [queryText, setQueryText] = useState<string>(options.initialQueryText ?? '');
    const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
    const normalizedQueryText: string = queryText.trim();
    const queryTerms: InfiniteScrollListSearchTerms = useMemo(
        () => tokenizeOmnisearchQueryText(normalizedQueryText),
        [normalizedQueryText]
    );
    const queryKey: InfiniteScrollListQueryKey<TQueryKeyPrefix> = useMemo(
        () =>
            [
                ...options.queryKeyPrefix,
                JSON.stringify(queryTerms),
                JSON.stringify(options.fieldPredicates ?? []),
                JSON.stringify(options.jsonPathPredicates ?? []),
            ] as const,
        [options.queryKeyPrefix, queryTerms, options.fieldPredicates, options.jsonPathPredicates]
    );

    const queryResult = useInfiniteQuery<
        InfiniteScrollListPage<TItem, TCursor>,
        Error,
        InfiniteData<InfiniteScrollListPage<TItem, TCursor>, TCursor | null>,
        InfiniteScrollListQueryKey<TQueryKeyPrefix>,
        TCursor | null
    >({
        queryKey,
        initialPageParam: null,
        queryFn: async ({ pageParam }) => {
            const page = await options.fetchPage({
                terms: queryTerms.length > 0 ? queryTerms : undefined,
                queryText: normalizedQueryText.length > 0 ? normalizedQueryText : undefined,
                fieldPredicates: options.fieldPredicates,
                jsonPathPredicates: options.jsonPathPredicates,
                limit: options.pageSize,
                cursor: options.parseCursorParam(pageParam),
            });
            if (page.pagination.has_more && page.pagination.next_cursor === undefined) {
                throw new Error(
                    'Infinite scroll pagination missing next_cursor when has_more is true'
                );
            }
            return page;
        },
        getNextPageParam: (lastPage) => {
            if (!lastPage.pagination.has_more) {
                return null;
            }
            return lastPage.pagination.next_cursor;
        },
        enabled: options.enabled,
        retry: options.retry ?? false,
        staleTime: options.staleTime ?? 0,
        gcTime: options.gcTime ?? 0,
    });

    const items: ReadonlyArray<TItem> = useMemo(() => {
        const pages: ReadonlyArray<InfiniteScrollListPage<TItem, TCursor>> =
            queryResult.data?.pages ?? [];
        return pages.flatMap((page) => page.items);
    }, [queryResult.data]);
    const fetchNextPage = useCallback(async (): Promise<void> => {
        await queryResult.fetchNextPage();
    }, [queryResult.fetchNextPage]);

    useEffect(() => {
        const sentinelElement: HTMLDivElement | null = loadMoreSentinelRef.current;
        if (sentinelElement === null) {
            return;
        }
        if (!queryResult.hasNextPage || queryResult.isFetchingNextPage) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries: ReadonlyArray<IntersectionObserverEntry>) => {
                const firstEntry: IntersectionObserverEntry | undefined = entries[0];
                if (firstEntry?.isIntersecting !== true) {
                    return;
                }
                if (!queryResult.hasNextPage || queryResult.isFetchingNextPage) {
                    return;
                }
                fetchNextPage().catch(() => undefined);
            },
            {
                root: options.observerRootRef?.current ?? null,
                rootMargin: options.rootMargin ?? '600px 0px',
                threshold: options.threshold ?? 0,
            }
        );
        observer.observe(sentinelElement);
        return () => {
            observer.disconnect();
        };
    }, [
        options.rootMargin,
        options.observerRootRef,
        options.threshold,
        options.parseCursorParam,
        fetchNextPage,
        queryResult.hasNextPage,
        queryResult.isFetchingNextPage,
    ]);

    return {
        queryText,
        setQueryText,
        items,
        isLoading: queryResult.isLoading,
        isFetching: queryResult.isFetching,
        isFetchingNextPage: queryResult.isFetchingNextPage,
        hasNextPage: queryResult.hasNextPage ?? false,
        fetchNextPage,
        error: queryResult.error ?? null,
        loadMoreSentinelRef,
        pagination: queryResult.data?.pages.at(-1)?.pagination ?? null,
    };
}
